import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Lock, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { JobMeta } from "@/components/jobs/JobParts";
import { Spinner } from "@/components/site/ProtectedRoute";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { EXPERIENCE } from "@/lib/community";

function ApplyBox({ job, onApplied }) {
  const [f, setF] = useState({ message: "", portfolio_url: "", whatsapp: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post(`/jobs/${job.id}/apply`, f); toast.success("Application sent"); onApplied(); } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="flex flex-col gap-3" data-testid="job-apply-form">
      <Textarea required minLength={20} rows={6} placeholder="Why you're a great fit, relevant experience, availability (min 20 characters)" value={f.message} onChange={set("message")} data-testid="job-apply-message" />
      <Input placeholder="Portfolio, LinkedIn or website (optional)" value={f.portfolio_url} onChange={set("portfolio_url")} data-testid="job-apply-portfolio" />
      <Input placeholder="WhatsApp (optional)" value={f.whatsapp} onChange={set("whatsapp")} data-testid="job-apply-whatsapp" />
      <button disabled={busy} className="rounded-xl bg-[#F08A3C] text-white px-5 py-3 font-semibold hover:bg-[#D96F28] disabled:opacity-60" data-testid="job-apply-submit">{busy ? "Sending..." : "Send application"}</button>
    </form>
  );
}

function ApplyPanel({ job, user, reload }) {
  const box = "bg-white rounded-2xl border border-[#0B2A4A]/10 p-6";
  if (job.is_owner) return <div className={box} data-testid="job-owner-note">This is your job post. See applications in <Link to="/jobs?tab=posts" className="font-semibold text-[#147C88]">My job posts</Link>.</div>;
  if (job.applied) return <div className={`${box} flex gap-2 text-emerald-700`} data-testid="job-applied-note"><CheckCircle2 className="h-5 w-5" /> You applied to this job.</div>;
  if (!user) return <div className={box} data-testid="job-apply-signin"><p className="text-slate-700">Sign in to apply.</p><Link to={`/login?next=/jobs/${job.id}`} className="mt-3 inline-block rounded-xl bg-[#0B2A4A] text-[#F5ECD7] px-5 py-2.5 font-semibold">Sign in / Create account</Link></div>;
  if (!job.is_member) return <div className={box} data-testid="job-apply-join"><p className="text-slate-700"><Lock className="inline h-4 w-4 mr-1 text-[#F08A3C]" />Applying is for approved community members.</p><Link to="/community" className="mt-3 inline-block rounded-xl bg-[#1A9AA8] text-white px-5 py-2.5 font-semibold">Join the community</Link></div>;
  return <div className={box}><h2 className="font-semibold text-[#0B2A4A] mb-3">Apply for this job</h2><ApplyBox job={job} onApplied={reload} /></div>;
}

const Section = ({ t, v }) => (v ? <div><h2 className="font-semibold text-[#0B2A4A]">{t}</h2><p className="mt-1 text-slate-700 whitespace-pre-wrap leading-relaxed">{v}</p></div> : null);

export default function JobDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [err, setErr] = useState("");
  const load = () => api.get(`/jobs/${id}`).then((r) => setJob(r.data)).catch((e) => setErr(formatApiError(e)));
  useEffect(() => { if (user !== null) load(); }, [id, user]); // eslint-disable-line react-hooks/exhaustive-deps
  useSeo({ title: job ? `${job.title} · ${job.company}` : "Remote job", path: `/jobs/${id}`, noindex: true });
  if (err) return <p className="text-center py-24 text-rose-600" data-testid="job-error">{err}</p>;
  if (!job) return <Spinner />;
  return (
    <article className="max-w-6xl mx-auto px-4 sm:px-8 py-10 grid lg:grid-cols-[1.6fr_1fr] gap-8" data-testid="job-detail-page">
      <div className="flex flex-col gap-5">
        <Link to="/jobs" className="text-sm font-semibold text-[#147C88] inline-flex items-center gap-1" data-testid="job-back-link"><ArrowLeft className="h-4 w-4" /> All jobs</Link>
        <p className="font-mono-ui text-xs tracking-[0.3em] text-[#F08A3C]">{job.company.toUpperCase()}</p>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0B2A4A]" data-testid="job-title">{job.title}</h1>
        <JobMeta j={job} />
        <p className="text-sm text-slate-500">{[EXPERIENCE[job.experience], job.timezone, job.skills].filter(Boolean).join(" · ")}</p>
        {job.full ? (
          <div className="flex flex-col gap-5" data-testid="job-full-details">
            <Section t="About the role" v={job.description} /><Section t="Responsibilities" v={job.responsibilities} /><Section t="Requirements" v={job.requirements} /><Section t="English level" v={job.english_level} />
            {job.company_website && <a href={job.company_website.startsWith("http") ? job.company_website : `https://${job.company_website}`} target="_blank" rel="noopener noreferrer nofollow" className="text-sm font-semibold text-[#147C88]" data-testid="job-company-website">Company website</a>}
          </div>
        ) : <p className="text-slate-700" data-testid="job-teaser">{job.teaser}</p>}
      </div>
      <aside className="lg:pt-12"><ApplyPanel job={job} user={user} reload={load} /></aside>
    </article>
  );
}
